import { Loader2, Save, Check, Copy, Trash, ChevronDown, ChevronUp, Globe, Users } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { RichTextEditor } from '@/components/ui/rich-text-editor'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useHasPermission } from '@/lib/hooks/usePermission'
import type { WritingAssistantListItem } from '@/lib/types/writing'
import type { CollaboratorInfo } from '@/lib/hooks/writingAssistant/useWritingAssistantContent'

interface WritingAssistantCardProps {
  writingAssistant: WritingAssistantListItem
  isClosedCrisis: boolean
  isMinimized: boolean
  selectedLanguage: string
  isTranslating: boolean
  isSaving: boolean
  manualSaveLoading: boolean
  saveSuccess: boolean
  additionalInstructions: string
  contentState: string
  savingStatusText: string | null
  /** Users currently viewing / editing this document (excluding self). */
  collaborators?: CollaboratorInfo[]
  onToggleMinimized: () => void
  onLanguageChange: (language: string) => void
  onContentChange: (content: string) => void
  onManualSave: () => void
  onAdditionalInstructionsChange: (instructions: string) => void
  onAdjustContent: () => void
  onCopy: () => void
  onDelete: () => void
}

export function WritingAssistantCard({
  writingAssistant: wa,
  isClosedCrisis,
  isMinimized,
  selectedLanguage,
  isTranslating,
  isSaving,
  manualSaveLoading,
  saveSuccess,
  additionalInstructions,
  contentState,
  savingStatusText,
  collaborators = [],
  onToggleMinimized,
  onLanguageChange,
  onContentChange,
  onManualSave,
  onAdditionalInstructionsChange,
  onAdjustContent,
  onCopy,
  onDelete,
}: WritingAssistantCardProps) {
  const { t } = useTranslation()

  // Permission check
  const canDeleteWriting = useHasPermission('writing.delete')
  const canCreateWriting = useHasPermission('writing.create')

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'ready':
        return 'bg-green-100 text-green-700'
      case 'processing':
        return 'bg-blue-100 text-blue-700'
      case 'queued':
        return 'bg-yellow-100 text-yellow-700'
      case 'failed':
        return 'bg-red-100 text-red-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  return (
    <Card className="overflow-hidden rounded-lg border-x shadow-sm md:shadow-md">
      <CardHeader className="pb-4 px-4 pt-4 md:px-6 md:pt-6">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-lg md:text-xl mb-2">{wa.contentType}</CardTitle>
            <div className="flex items-center gap-2 md:gap-4 text-xs md:text-sm text-muted-foreground flex-wrap">
              {wa.createdBy && (
                <>
                  <span>
                    Door{' '}
                    {wa.createdBy.displayName ||
                      `${wa.createdBy.firstName} ${wa.createdBy.lastName}`}
                  </span>
                  <span>•</span>
                </>
              )}
              <span>{new Date(wa.createdAt).toLocaleString('nl-NL')}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Real-time collaborators presence indicator */}
            {collaborators.length > 0 && (
              <div
                className="flex items-center gap-1 text-xs text-muted-foreground"
                title={collaborators.map(c => c.userName || c.userId).join(', ')}
                aria-label={`${collaborators.length} andere gebruiker${collaborators.length !== 1 ? 's' : ''} bekijkt dit document`}
              >
                <Users className="w-3 h-3" />
                <div className="flex -space-x-1">
                  {collaborators.slice(0, 3).map(c => (
                    <div
                      key={c.userId}
                      className="w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-medium ring-1 ring-background"
                      title={c.userName || c.userId}
                    >
                      {(c.userName || c.userId).charAt(0).toUpperCase()}
                    </div>
                  ))}
                </div>
                {collaborators.length > 3 && (
                  <span>+{collaborators.length - 3}</span>
                )}
              </div>
            )}
            <span className={`px-2 py-1 text-xs rounded-md ${getStatusBadgeClass(wa.status)}`}>
              {t(`writing.status.${wa.status}`)}
            </span>
            <div
              onClick={onToggleMinimized}
              className="h-8 w-8 flex items-center justify-center rounded-md hover:bg-accent cursor-pointer transition-colors"
              title={isMinimized ? 'Uitvouwen' : 'Inklappen'}
            >
              {isMinimized ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground hover:text-foreground" />
              ) : (
                <ChevronUp className="w-4 h-4 text-muted-foreground hover:text-foreground" />
              )}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent
        className={`space-y-4 transition-all duration-300 ease-in-out overflow-hidden px-4 md:px-6 ${
          isMinimized ? 'max-h-0 opacity-0 py-0' : 'opacity-100 pb-4 md:pb-6'
        }`}
      >
        {isClosedCrisis && (
          <div className="mb-2 p-2 bg-muted/50 rounded-md border">
            <p className="text-sm text-muted-foreground flex items-center gap-2">
              <span>🔒</span>
              Deze crisis is gesloten — bewerken en opslaan zijn uitgeschakeld.
            </p>
          </div>
        )}

        {wa.status === 'queued' || wa.status === 'processing' ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mr-2" />
            <span className="text-muted-foreground">Content wordt gegenereerd...</span>
          </div>
        ) : wa.status === 'failed' ? (
          <div className="text-center py-12 text-red-600">
            Genereren mislukt. Probeer opnieuw te genereren of verwijder het item.
          </div>
        ) : (
          <>
            {/* Content Editor or Translation Loading */}
            {selectedLanguage !== 'nl' && isTranslating ? (
              <div className="flex items-center justify-center py-8 border rounded-md bg-muted/20">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground mr-2" />
                <span className="text-muted-foreground">Vertaling wordt gegenereerd...</span>
              </div>
            ) : (
              <RichTextEditor
                value={contentState}
                onChange={onContentChange}
                className={isClosedCrisis ? 'opacity-75 pointer-events-none' : ''}
                disabled={!canCreateWriting || isClosedCrisis}
                headerRight={
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-muted-foreground" />
                    <Select value={selectedLanguage} onValueChange={onLanguageChange}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nl">Nederlands</SelectItem>
                        <SelectItem value="en">Engels</SelectItem>
                        <SelectItem value="de">Duits</SelectItem>
                        <SelectItem value="es">Spaans</SelectItem>
                        <SelectItem value="fr">Frans</SelectItem>
                        <SelectItem value="pt">Portugees</SelectItem>
                        <SelectItem value="it">Italiaans</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                }
              />
            )}

            {/* Additional Instructions */}
            {wa.status === 'ready' && !isClosedCrisis && canCreateWriting && (
              <div className="space-y-2">
                <label className="text-xs md:text-sm font-medium text-foreground">
                  Aanvullende instructies voor aanpassing (alleen voor Nederlands)
                </label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    placeholder="Bijv. 'Maak het korter' of 'Voeg meer details toe over...' "
                    value={additionalInstructions}
                    onChange={e => onAdditionalInstructionsChange(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        onAdjustContent()
                      }
                    }}
                    className="flex-1"
                  />
                  <Button
                    variant="gradient"
                    size="lg"
                    onClick={onAdjustContent}
                    disabled={!additionalInstructions || !additionalInstructions.trim()}
                  >
                    Aanpassen
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Action Buttons (always visible, even for failed items) */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-0 pt-4 border-t">
          <div className="hidden md:flex items-center gap-2 md:gap-4 flex-wrap">
            {/* Manual Save Button */}
            {wa.status === 'ready' && !isClosedCrisis && canCreateWriting && (
              <Button
                variant="default"
                size="sm"
                onClick={onManualSave}
                loading={manualSaveLoading}
              >
                {saveSuccess ? (
                  <Check className="w-4 h-4 mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Opslaan
              </Button>
            )}

            {/* Saving Status */}
            {savingStatusText && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {isSaving ? (
                  <>
                    <div className="w-4 h-4 rounded-full border-2 border-t-transparent border-primary animate-spin" />
                    <span>{savingStatusText}</span>
                  </>
                ) : (
                  <span>{savingStatusText}</span>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Copy Button */}
            {wa.status === 'ready' && !isClosedCrisis && (
              <Button
                variant="default"
                size="sm"
                onClick={onManualSave}
                loading={manualSaveLoading}
                className="md:hidden"
              >
                {saveSuccess ? (
                  <Check className="w-4 h-4 mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Opslaan
              </Button>
            )}

            {contentState && contentState.trim() && (
              <Button variant="outline" size="sm" onClick={onCopy}>
                <Copy className="w-4 h-4 mr-2" />
                Kopiëren
              </Button>
            )}

            {/* Delete Button (always available unless crisis is closed) */}
            {canDeleteWriting && (
              <Button variant="outline" size="sm" onClick={onDelete} disabled={isClosedCrisis}>
                <Trash className="w-4 h-4 mr-2" />
                Verwijderen
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
